// The cloud a piece leaves when it goes from the board. Puffs start at the
// middle of the square and go out in every direction. Each puff is solid, then
// half dither, then a quarter, then gone, so the cloud thins and does not fade.
// The draws are from `Math.random`: a cloud is presentation, and a draw from
// the match source would change the replay of a saved match.

import {
  CLOUD_CORE_RADIUS_PX,
  CLOUD_MS,
  CLOUD_PUFF_COUNT,
  CLOUD_PUFF_RADIUS_PX,
  CLOUD_PUFF_REACH_PX,
  CLOUD_PUFF_STAGGER_MS,
  CLOUD_THIN_PORTIONS,
  puffDitherId,
  SQUARE_PX,
} from '../constants';
import type { Owner } from '../engine/types';
import { svg } from '../lib/svg';
import { boardOrigin } from './boardOrigin';

const FULL_TURN = Math.PI * 2;

const within = ([low, high]: readonly [number, number]): number =>
  low + Math.random() * (high - low);

/**
 * One puff. It moves and grows by CSS. It moves along the two axes and does not turn,
 * so the cells of its dither stay level, like every other dither on the board.
 */
const puff = (angle: number, reach: number, radius: number, delayMs: number): SVGCircleElement => {
  const circle = svg('circle', { class: 'cloud__puff', cx: 0, cy: 0, r: 0 });
  circle.style.setProperty('--puff-x', `${Math.cos(angle) * reach}px`);
  circle.style.setProperty('--puff-y', `${Math.sin(angle) * reach}px`);
  circle.style.setProperty('--puff-radius', `${radius}px`);
  circle.style.animationDelay = `${delayMs}ms`;
  return circle;
};

/** A cloud on a square, in the color of the side that lost the piece. It removes itself. */
export const createCloud = (square: number, owner: Owner): SVGGElement => {
  const origin = boardOrigin(square);
  const slice = FULL_TURN / CLOUD_PUFF_COUNT;
  const start = Math.random() * slice;
  const puffs = Array.from({ length: CLOUD_PUFF_COUNT }, (_, index) =>
    puff(
      start + slice * (index + Math.random() / 2),
      within(CLOUD_PUFF_REACH_PX),
      within(CLOUD_PUFF_RADIUS_PX),
      Math.random() * CLOUD_PUFF_STAGGER_MS,
    ),
  );
  const root = svg(
    'g',
    {
      class: `cloud cloud--${owner}`,
      transform: `translate(${origin.x + SQUARE_PX / 2} ${origin.y + SQUARE_PX / 2})`,
    },
    [puff(0, 0, CLOUD_CORE_RADIUS_PX, 0), ...puffs],
  );
  root.style.setProperty('--cloud-ms', `${CLOUD_MS}ms`);
  const [toHalf, toQuarter] = CLOUD_THIN_PORTIONS;
  // A dither takes its pattern by id. The pattern has the color of the owner.
  const thinTo = (paint: 'half' | 'quarter') => (): void =>
    root.style.setProperty('--puff-paint', `url("#${puffDitherId(owner, paint)}")`);
  setTimeout(thinTo('half'), CLOUD_MS * toHalf);
  setTimeout(thinTo('quarter'), CLOUD_MS * toQuarter);
  setTimeout(() => root.remove(), CLOUD_MS + CLOUD_PUFF_STAGGER_MS);
  return root;
};
